import { XIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import Text from './Text';

const WIDTH = {
  form: 520,
  confirm: 440,
};

export type ModalSize = keyof typeof WIDTH;

type Props = {
  /** Omitted by a confirm dialog, which carries its title in the body instead. */
  title?: string;
  size?: ModalSize;
  children: React.ReactNode;
  onClose: () => void;
  fullScreenOnMobile?: boolean;
};

export default function Modal({
  title,
  size = 'form',
  children,
  onClose,
  fullScreenOnMobile,
}: Props) {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const width = WIDTH[size];

  return (
    <html.div style={styles.backdrop} onClick={onClose}>
      <html.div
        style={[
          styles.modal,
          fullScreenOnMobile
            ? styles.fullScreenOnMobile(width)
            : styles.sized(width),
        ]}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <html.div style={[Style.row, Style.alignCenter, styles.header]}>
            <html.span style={Style.fill}>
              <Text typeface="headline" color="neutral100">
                {title}
              </Text>
            </html.span>
            <html.button
              style={styles.close}
              onClick={onClose}
              aria-label="Close"
            >
              <XIcon size={16} color={Color.neutral100} />
            </html.button>
          </html.div>
        )}
        {children}
      </html.div>
    </html.div>
  );
}

const styles = css.create({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: color.neutral0,
    borderRadius: 16,
    overflowX: 'hidden',
    overflowY: 'auto',
    boxShadow:
      '0 24px 48px -8px rgba(31,36,51,0.18), 0 2px 8px rgba(31,36,51,0.1)',
  },
  sized: (width: number) => ({
    width,
    maxWidth: '90vw',
    maxHeight: '90vh',
  }),
  fullScreenOnMobile: (width: number) => ({
    width: {
      default: width,
      '@media (max-width: 768px)': '100vw',
    },
    maxWidth: {
      default: '90vw',
      '@media (max-width: 768px)': '100vw',
    },
    height: {
      default: 'auto',
      '@media (max-width: 768px)': '100vh',
    },
    maxHeight: {
      default: '90vh',
      '@media (max-width: 768px)': '100vh',
    },
    borderRadius: {
      default: 16,
      '@media (max-width: 768px)': 0,
    },
  }),
  header: {
    gap: 8,
    paddingTop: 20,
    paddingInline: 20,
  },
  close: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 28,
    height: 28,
    borderWidth: 0,
    borderRadius: 6,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
});
